import React, { useEffect, useState, useCallback, useRef } from "react";
import { FaDownload, FaTimes, FaCheck, FaEye } from "react-icons/fa";

import { Input, Select } from "@/components/Form";
import { Page, DashboardContainer, Row, Col } from "@/components/Grid";
import { AdminMenu } from "@/components/Admin";
import { Navbar } from "@/components/Blocks";
import Paginator, { Loading } from "@/components/Paginator";
import { Modal, Button } from "@/components/Common";
import { useBoletos } from "@/hooks";
import toastify from "@/services/toastify";

import {
  Header,
  FiltroBasico,
  ActionsContainer,
  Totais,
  BoletoListItem,
  ActionButton,
  StatusBadge,
  ModalContent,
  ModalBody,
  ModalFooter,
} from "./styles";

export default function Boletos({ history }) {
  const { cancelBoleto, approveBoleto, approveAllBoletos } = useBoletos();
  const [boletos, setBoletos] = useState([]);
  const [readyToPaginate, setReadyToPaginate] = useState(false);
  const [query, setQuery] = useState({ filters: "boletos,todos", status: "" });
  const [paginator, setPaginator] = useState(null);
  const paginatorRef = useRef();
  const [loadingBoletos, setLoadingBoletos] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [boletoToCancel, setBoletoToCancel] = useState(null);
  const [cancelingBoleto, setCancelingBoleto] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [boletoToApprove, setBoletoToApprove] = useState(null);
  const [approvingBoleto, setApprovingBoleto] = useState(false);
  const [openApproveAllModal, setOpenApproveAllModal] = useState(false);
  const [approvingAllBoletos, setApprovingAllBoletos] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [boletoDetails, setBoletoDetails] = useState(null);

  function handleOnFetchComplete(data) {
    setBoletos(data.data);
    setPaginator(data.paginator);
  }

  function handleOnChange(event) {
    const { name, value } = event.target;
    setQuery(state => {
      return { ...state, [name]: value };
    });
  }

  function renderHeader() {
    return (
      <Header>
        <h4>Financeiro</h4>
        <h2>Todos os boletos</h2>
        <div className="breadcrumbs">
          Financeiro {">"} <span>Todos os boletos</span>
        </div>
      </Header>
    );
  }

  function renderFiltroBasico() {
    const colNome = {
      lg: 0.7,
      md: 0.5,
      sm: 1,
    };

    const colStatus = {
      lg: 0.3,
      md: 0.5,
      sm: 1,
    };

    const statusOptions = [
      { id: '', title: 'Todos os status' },
      { id: 'OPEN', title: 'Em aberto' },
      { id: 'PAID', title: 'Pago' },
      { id: 'LATE', title: 'Vencido' },
      { id: 'CANCELED', title: 'Cancelado' },
      { id: 'DRAFT', title: 'Rascunho' },
    ];

    return (
      <FiltroBasico>
        <Row hascols>
          <Col {...colNome}>
            <Input
              label="Nome da empresa"
              name="nome"
              placeholder="Busque pelo nome da empresa"
              onChange={handleOnChange}
            />
          </Col>
          <Col {...colStatus}>
            <Select
              label="Status"
              name="status"
              value={query.status || ""}
              defaultValue=" "
              options={statusOptions}
              onChange={handleOnChange}
            />
          </Col>
        </Row>
      </FiltroBasico>
    );
  }

  function renderTotais() {
    if (loadingBoletos) return <></>;
    if (paginator !== null) {
      const { total } = paginator;
      if (total === 0) {
        return (
          <Totais>
            Sem boletos para exibir dentro dos critérios de busca
          </Totais>
        );
      }
      return (
        <Totais>
          Exibindo {boletos.length} de {total} boletos
        </Totais>
      );
    }
  }

  function formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100); // Dividindo por 100 porque o valor vem em centavos
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('pt-BR');
  }

  function getStatusColor(status) {
    switch (status) {
      case 'OPEN': return '#f39c12';
      case 'PAID': return '#27ae60';
      case 'LATE': return '#e74c3c';
      case 'CANCELED': return '#95a5a6';
      case 'DRAFT': return '#3498db';
      default: return '#95a5a6';
    }
  }

  function getStatusText(status) {
    switch (status) {
      case 'OPEN': return 'Em aberto';
      case 'PAID': return 'Pago';
      case 'LATE': return 'Vencido';
      case 'CANCELED': return 'Cancelado';
      case 'DRAFT': return 'Rascunho';
      default: return status;
    }
  }

  function handleDownloadBoleto(boleto) {
    if (boleto.bank_slip_url) {
      window.open(boleto.bank_slip_url, '_blank');
    }
  }

  function handleCancelBoleto(boleto) {
    setBoletoToCancel(boleto);
    setOpenCancelModal(true);
  } async function confirmCancelBoleto() {
    if (!boletoToCancel) return;

    setCancelingBoleto(true);

    const result = await cancelBoleto(boletoToCancel.id);

    if (result.success) {
      toastify("Boleto cancelado com sucesso!");
      setOpenCancelModal(false);
      setBoletoToCancel(null);
      setCancelingBoleto(false);

      // Recarregar a página uma única vez
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      toastify(result.message || 'Erro ao cancelar boleto', "error");
      setCancelingBoleto(false);
    }
  }

  function handleCloseCancelModal() {
    if (cancelingBoleto) return; // Não permite fechar durante o loading
    setOpenCancelModal(false);
    setBoletoToCancel(null);
  }

  function handleApproveBoleto(boleto) {
    setBoletoToApprove(boleto);
    setOpenApproveModal(true);
  }

  async function confirmApproveBoleto() {
    if (!boletoToApprove) return;

    setApprovingBoleto(true);

    const result = await approveBoleto(boletoToApprove.id);

    if (result.success) {
      toastify("Boleto aprovado com sucesso!");
      setOpenApproveModal(false);
      setBoletoToApprove(null);
      setApprovingBoleto(false);

      // Recarregar a página uma única vez
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      toastify(result.message || 'Erro ao aprovar boleto', "error");
      setApprovingBoleto(false);
    }
  }

  function handleCloseApproveModal() {
    if (approvingBoleto) return; // Não permite fechar durante o loading
    setOpenApproveModal(false);
    setBoletoToApprove(null);
  }

  async function confirmApproveAllBoletos() {
    setApprovingAllBoletos(true);

    const result = await approveAllBoletos();

    if (result.success) {
      toastify("Boletos aprovados com sucesso!");
      setOpenApproveAllModal(false);
      setApprovingAllBoletos(false);

      if (paginatorRef.current) {
        paginatorRef.current.resetAndFetchData();
      }
      return;
    }

    toastify(result.message || "Erro ao aprovar boletos", "error");
    setApprovingAllBoletos(false);
  }

  function handleCloseApproveAllModal() {
    if (approvingAllBoletos) return;
    setOpenApproveAllModal(false);
  }

  function handleViewDetails(boleto) {
    setBoletoDetails(boleto);
    setOpenDetailsModal(true);
  }

  function handleCloseDetailsModal() {
    setOpenDetailsModal(false);
    setBoletoDetails(null);
  }

  function renderDetailsModal() {
    if (!boletoDetails) return null;

    const parseDescricao = (descricao) => {
      try {
        return typeof descricao === 'string' ? JSON.parse(descricao) : descricao;
      } catch {
        return [];
      }
    };

    const itens = parseDescricao(boletoDetails.descricao) || [];

    const groupedItens = itens.reduce((acc, item) => {
      const tipo = item.tipo || 'outros';
      if (!acc[tipo]) acc[tipo] = [];
      acc[tipo].push(item);
      return acc;
    }, {});

    const getTipoLabel = (tipo) => {
      switch (tipo) {
        case 'remuneracao': return 'Remuneração';
        case 'beneficio': return 'Benefícios';
        case 'valor_contrato_empresa': return 'Valor do Contrato';
        default: return 'Outros';
      }
    };

    return (
      <Modal open={openDetailsModal}>
        <ModalContent style={{ maxWidth: '800px', width: '90%' }}>
          <ModalBody>
            <h3>Detalhes do Boleto</h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                <div>
                  <strong>Empresa:</strong><br />
                  {boletoDetails.empresa.razao_social || boletoDetails.empresa.nome_exibicao}
                </div>
                {boletoDetails.status !== 'DRAFT' && (
                  <div>
                    <strong>Código:</strong><br />
                    {boletoDetails.code}
                  </div>
                )}
                <div>
                  <strong>Data de Criação:</strong><br />
                  {formatDate(boletoDetails.created_at)}
                </div>
                <div>
                  <strong>Data de Vencimento:</strong><br />
                  {formatDate(boletoDetails.due_date)}
                </div>
                <div>
                  <strong>Valor Total:</strong><br />
                  <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#27ae60' }}>
                    {formatCurrency(boletoDetails.total_amount)}
                  </span>
                </div>
                <div>
                  <strong>Status:</strong><br />
                  <StatusBadge color={getStatusColor(boletoDetails.status)}>
                    {getStatusText(boletoDetails.status)}
                  </StatusBadge>
                </div>
              </div>
            </div>

            {itens.length > 0 && (
              <div style={{ marginBottom: '15px', marginTop: '30px' }}>
                {Object.entries(groupedItens).map(([tipo, items]) => (
                  <div key={tipo}>
                    <div style={{ background: '#f8f9fa', borderRadius: '5px' }}>
                      {items.map((item, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 20px',
                          borderBottom: '1px solid #dee2e6'
                        }}>
                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <div style={{ fontWeight: '500' }}>{item.descricao}</div>
                            {item.tce_id && (
                              <div style={{ fontSize: '12px', color: '#666' }}>TCE: {item.tce_id}</div>
                            )}
                          </div>
                          <div style={{ fontWeight: 'bold', color: '#27ae60' }}>
                            {formatCurrency(parseFloat(item.valor) * 100)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

          </ModalBody>
          <ModalFooter>
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <Button
                  onClick={handleCloseDetailsModal}
                  variant="cancel"
                >
                  Fechar
                </Button>
                {boletoDetails.status === 'DRAFT' && (
                  <Button
                    onClick={() => {
                      handleCloseDetailsModal();
                      handleApproveBoleto(boletoDetails);
                    }}
                    variant="cyan"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <FaCheck size={14} /> Aprovar
                  </Button>
                )}
                {boletoDetails.bank_slip_url && boletoDetails.status !== 'PAID' && boletoDetails.status !== 'CANCELED' && (
                  <Button
                    onClick={() => {
                      handleCloseDetailsModal();
                      handleDownloadBoleto(boletoDetails);
                    }}
                    variant="cyan"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <FaDownload size={14} /> Baixar
                  </Button>
                )}
                {boletoDetails.status !== 'PAID' && boletoDetails.status !== 'CANCELED' && boletoDetails.status !== 'DRAFT' && (
                  <Button
                    onClick={() => {
                      handleCloseDetailsModal();
                      handleCancelBoleto(boletoDetails);
                    }}
                    variant="red"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <FaTimes size={14} /> Cancelar
                  </Button>
                )}
              </div>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal >
    );
  }

  function renderBoletos() {
    return boletos.map(boleto => {
      return (
        <BoletoListItem key={boleto.id}>
          <div className="coluna coluna-empresa">
            <h4>{boleto.empresa.razao_social || boleto.empresa.nome_exibicao}</h4>
            {boleto.status !== 'DRAFT' && (
              <p>Código: {boleto.code}</p>
            )}
          </div>
          <div className="coluna coluna-data">
            <h4>Data</h4>
            {formatDate(boleto.created_at)}
          </div>
          <div className="coluna coluna-data">
            <h4>Vencimento</h4>
            {formatDate(boleto.due_date)}
          </div>
          <div className="coluna coluna-valor">
            <h4>Valor</h4>
            {formatCurrency(boleto.total_amount)}
          </div>
          <div className="coluna coluna-status">
            <h4>Status</h4>
            <StatusBadge color={getStatusColor(boleto.status)}>
              {getStatusText(boleto.status)}
            </StatusBadge>
          </div>
          <div className="coluna coluna-actions">
            <ActionButton onClick={() => handleViewDetails(boleto)}>
              <FaEye color="#6c757d" size={14} /> <span>Detalhes</span>
            </ActionButton>
            {boleto.status === 'DRAFT' && (
              <ActionButton onClick={() => handleApproveBoleto(boleto)}>
                <FaCheck color="#27ae60" size={14} /> <span>Aprovar</span>
              </ActionButton>
            )}
            {/* {boleto.bank_slip_url && boleto.status !== 'PAID' && boleto.status !== 'CANCELED' && (
              <ActionButton onClick={() => handleDownloadBoleto(boleto)}>
                <FaDownload color="#271946" size={14} /> <span>Baixar</span>
              </ActionButton>
            )} */}
            {boleto.status !== 'PAID' && boleto.status !== 'CANCELED' && boleto.status !== 'DRAFT' && (
              <ActionButton onClick={() => handleCancelBoleto(boleto)}>
                <FaTimes color="#e74c3c" size={14} /> <span>Cancelar</span>
              </ActionButton>
            )}
          </div>
        </BoletoListItem>
      );
    });
  }

  const setQueryCB = useCallback(() => {
    setQuery(state => ({ ...state, filters: "boletos,todos", status: "" }));
    setReadyToPaginate(true);
  }, []);

  useEffect(() => {
    setQueryCB();
  }, [setQueryCB]);

  return (
    <Page>
      <Navbar type="interna" />
      <AdminMenu />
      <DashboardContainer>
        <Row>
          <Col lg={0.8} md={0.7} sm={1} style={{ margin: '0 auto' }}>
            {renderHeader()}
            {renderFiltroBasico()}
            {renderTotais()}
            <div>
              {loadingBoletos ? (
                <Loading />
              ) : (
                renderBoletos()
              )}
            </div>
            <ActionsContainer>
              <Button
                variant="tangerine"
                onClick={() => setOpenApproveAllModal(true)}
                disabled={loadingBoletos || approvingAllBoletos || !paginator || paginator.total === 0}
              >
                Aprovar todos
              </Button>
            </ActionsContainer>
            {readyToPaginate && (
              <Paginator
                endpoint="boletos"
                query={query}
                onFetchComplete={handleOnFetchComplete}
                onLoading={l => setLoadingBoletos(l)}
                ref={paginatorRef}
              />
            )}
          </Col>
        </Row>
      </DashboardContainer>

      <Modal open={openCancelModal}>
        <ModalContent>
          <ModalBody>
            <h3>Confirmar Cancelamento</h3>
            <p>
              Tem certeza que deseja cancelar o boleto{' '}
              <strong>{boletoToCancel?.code}</strong>?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={handleCloseCancelModal}
              variant="cancel"
              disabled={cancelingBoleto}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmCancelBoleto}
              variant="red"
              loading={cancelingBoleto}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={openApproveModal}>
        <ModalContent>
          <ModalBody>
            <h3>Confirmar Aprovação</h3>
            <p>
              Tem certeza que deseja aprovar o boleto{' '}
              <strong>{boletoToApprove?.empresa?.razao_social || boletoToApprove?.empresa?.nome_exibicao}</strong>?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={handleCloseApproveModal}
              variant="cancel"
              disabled={approvingBoleto}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmApproveBoleto}
              variant="cyan"
              loading={approvingBoleto}
            >
              Aprovar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={openApproveAllModal}>
        <ModalContent>
          <ModalBody>
            <h3>Confirmar Aprovação</h3>
            <p>
              Tem certeza que deseja aprovar todos os boletos em rascunho?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={handleCloseApproveAllModal}
              variant="cancel"
              disabled={approvingAllBoletos}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmApproveAllBoletos}
              variant="tangerine"
              loading={approvingAllBoletos}
            >
              Aprovar todos
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {renderDetailsModal()}
    </Page>
  );
}
